import React, { useState, useEffect } from 'react';
import '../../styles/admin/AdminRefund.css'; // AdminRefund.css로 변경
import { useNavigate } from 'react-router-dom';
import { getRefundRequests } from '../../utils/api';

const AdminRefund = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('업체명');
  const navigate = useNavigate();
  
  // 상태 정의
  const [refundRequests, setRefundRequests] = useState([]);
  const [allRefundRequests, setAllRefundRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const itemsPerPage = 6; // 페이지당 6개 항목만 표시

  useEffect(() => {
    fetchRefundRequests();
  }, []);
  
  // 검색어가 변경될 때마다 필터링
  useEffect(() => {
    if (allRefundRequests.length > 0) {
      filterRefundRequests();
    }
  }, [searchTerm, allRefundRequests, selectedOption]);
  
  // API에서 환불 요청 데이터 가져오기
  const fetchRefundRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // API 호출
      const response = await getRefundRequests();
      console.log('환불 요청 목록 응답:', response);
      
      // 응답 데이터 처리
      if (response && response.status === 'success' && response.data && response.data.refunds) {
        const refundsData = response.data.refunds || [];
        
        // 데이터 형식 변환
        const formattedRefunds = refundsData.map(item => {
          return {
            id: item.id || Math.random().toString(36).substr(2, 9),
            name: item.company_name || '이름 없음',
            requestDate: item.request_date ? item.request_date.split('T')[0] : '-',
            plan: item.plan_name || '미상',
            period: item.plan_period || '미상',
            refundAmount: item.refund_amount ? `₩ ${item.refund_amount.toLocaleString()}` : '미상',
            reason: item.reason || '-',
            payment_id: item.payment_id,
            company_code: item.company_code,
            status: item.status
          };
        });
        
        // 추가 정보 로깅
        console.log('환불 요청 건수:', formattedRefunds.length);
        console.log('변환된 환불 요청 데이터:', formattedRefunds);
        
        setAllRefundRequests(formattedRefunds);
        setRefundRequests(formattedRefunds);
        setTotalCount(formattedRefunds.length);
      } else {
        console.warn('유효한 환불 요청 데이터가 없습니다:', response);
        setAllRefundRequests([]);
        setRefundRequests([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('환불 요청 데이터 로딩 오류:', err);
      setError(err.message || '환불 요청 데이터를 불러오는 중 오류가 발생했습니다');
      
      // API 오류 시 빈 데이터 설정
      setAllRefundRequests([]);
      setRefundRequests([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  
  // 검색어 및 정렬 옵션에 따라 데이터 필터링
  const filterRefundRequests = () => {
    let filtered = [...allRefundRequests];
    
    // 검색어로 필터링
    if (searchTerm) {
      filtered = filtered.filter(request => 
        request.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // 정렬 옵션 적용
    filtered = sortRefunds(filtered, selectedOption);
    
    setRefundRequests(filtered);
    setTotalCount(filtered.length);
    setCurrentPage(1); // 필터링 시 첫 페이지로 이동
  };
  
  // 선택된 정렬 옵션에 따라 환불 요청 정렬
  const sortRefunds = (refunds, sortOption) => {
    const sortedRefunds = [...refunds];
    
    switch (sortOption) {
      case 'Index':
        // 기본 상태 유지 (원래 순서)
        return sortedRefunds;
      
      case '업체명':
        return sortedRefunds.sort((a, b) => a.name.localeCompare(b.name, 'ko-KR'));
      
      case '환불 요청일':
        return sortedRefunds.sort((a, b) => {
          // 날짜 비교
          if (a.requestDate === '-' && b.requestDate === '-') return 0;
          if (a.requestDate === '-') return 1;
          if (b.requestDate === '-') return -1;
          
          // 날짜 문자열을 Date 객체로 변환하여 비교
          return new Date(b.requestDate) - new Date(a.requestDate);
        });
      
      case '환불 요청 플랜':
        return sortedRefunds.sort((a, b) => a.plan.localeCompare(b.plan, 'ko-KR'));
      
      case '환불 금액':
        return sortedRefunds.sort((a, b) => {
          // 환불 금액에서 숫자만 추출하여 비교
          const refundAmountA = parseInt(a.refundAmount.replace(/[^\d]/g, '')) || 0;
          const refundAmountB = parseInt(b.refundAmount.replace(/[^\d]/g, '')) || 0;
          return refundAmountB - refundAmountA; // 내림차순
        });
      
      case '환불 사유':
        return sortedRefunds.sort((a, b) => a.reason.localeCompare(b.reason, 'ko-KR'));
      
      default:
        return sortedRefunds;
    }
  };
  
  // 현재 페이지에 표시할 데이터 계산
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return refundRequests.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const selectOption = (option) => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePlanClick = (plan, id) => {
    // 환불 상세 페이지로 이동
    console.log('환불 요청 ID:', id);
    navigate(`/admin/refund-detail/${id}`);
  };

  // 환불 규정 확인하기 버튼 클릭 핸들러
  const handleCheckPolicy = () => {
    // 여기에 환불 규정 페이지로 이동하는 코드 추가
    window.open('/admin/refund-policy', '_blank');
  };

  return (
    <div className="adminCompanyListContainer" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: 'calc(100vh - 80px)'
    }}>
      <div className="contentArea">
        {/* 로딩 인디케이터 */}
        {loading && (
          <div className="loadingMessage" style={{ textAlign: 'center', padding: '20px' }}>
            환불 요청 데이터를 불러오는 중입니다...
          </div>
        )}
        
        {/* 에러 메시지 */}
        {error && (
          <div className="errorMessage" style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
            {error}
          </div>
        )}
      
        {/* 환불 규정 확인하기 버튼 */}
        <div className="refund-policy-container" style={{ marginBottom: '20px' }}>
          <button className="check-policy-button" onClick={handleCheckPolicy}>
            <span>환불 규정 확인하기</span>
            <span className="arrow-icon">&gt;</span>
          </button>
        </div>
      
        <div className="filterSection">
          <div className="filterLeft">
            <div className="filterItem">
              <label className="filterLabel">정렬</label>
              <div className="dropdown-container">
                <div className="dropdown-header" onClick={toggleDropdown}>
                  <span>{selectedOption}</span>
                  <span className={`arrow-icon ${isDropdownOpen ? 'up' : 'down'}`}>
                    &#9650;
                  </span>
                </div>
                {isDropdownOpen && (
                  <div className="dropdown-menu">
                    <div 
                      className={`dropdown-item ${selectedOption === 'Index' ? 'selected' : ''}`}
                      onClick={() => selectOption('Index')}
                    >
                      Index
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '업체명' ? 'selected' : ''}`}
                      onClick={() => selectOption('업체명')}
                    >
                      업체명
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '환불 요청일' ? 'selected' : ''}`}
                      onClick={() => selectOption('환불 요청일')}
                    >
                      환불 요청일
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '환불 요청 플랜' ? 'selected' : ''}`}
                      onClick={() => selectOption('환불 요청 플랜')}
                    >
                      환불 요청 플랜
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '환불 금액' ? 'selected' : ''}`}
                      onClick={() => selectOption('환불 금액')}
                    >
                      환불 금액
                    </div>
                    <div 
                      className={`dropdown-item ${selectedOption === '환불 사유' ? 'selected' : ''}`}
                      onClick={() => selectOption('환불 사유')}
                    >
                      환불 사유
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="filterRight">
            <div className="filterItem">
              <label className="filterLabel">업체명 검색</label>
              <div className="searchWrapper" style={{ width: '215px' }}>
                <input
                  type="text"
                  className="searchInput"
                  style={{ height: '45px' }}
                  placeholder="업체명 입력"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="searchButton">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="black"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="totalRecords">총 {totalCount}건</div>
        
        {/* 데이터가 없는 경우 메시지 표시 */}
        {!loading && refundRequests.length === 0 && (
          <div className="emptyMessage" style={{ textAlign: 'center', padding: '30px' }}>
            조회된 환불 요청이 없습니다.
          </div>
        )}
        
        <div className="tableContainer">
          {!loading && refundRequests.length > 0 && (
            <table className="dataTable">
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>Index</th>
                  <th style={{ width: '18%' }}>업체명</th>
                  <th style={{ width: '12%' }}>환불 요청일</th>
                  <th style={{ width: '18%' }}>환불 요청 플랜</th>
                  <th style={{ width: '22%' }}>플랜 적용 기간</th>
                  <th style={{ width: '12%' }}>환불 금액</th>
                  <th style={{ width: '13%' }}>환불 사유</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentPageData().map((request, index) => (
                  <tr key={request.id}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{request.name}</td>
                    <td>{request.requestDate}</td>
                    <td>
                      <span 
                        className="plan-link" 
                        onClick={() => handlePlanClick(request.plan, request.id)}
                      >
                        {request.plan}
                      </span>
                    </td>
                    <td>{request.period}</td>
                    <td>{request.refundAmount}</td>
                    <td>{request.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      {!loading && totalPages > 0 && (
        <div className="paginationArea" style={{ marginTop: 'auto', paddingTop: '50px', paddingBottom: '30px' }}>
          <div className="pagination">
            <button 
              className="pageNav" 
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            
            {Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
              // 현재 페이지를 중심으로 표시할 페이지 번호 계산
              let startPage = Math.max(1, currentPage - 4);
              let endPage = Math.min(startPage + 9, totalPages);
              
              if (endPage - startPage < 9) {
                startPage = Math.max(1, endPage - 9);
              }
              
              return i + startPage <= endPage ? i + startPage : null;
            })
            .filter(page => page !== null)
            .map(page => (
              <button 
                key={page}
                className={`pageNumber ${currentPage === page ? 'active' : ''}`} 
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            
            <button 
              className="pageNav" 
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRefund; 